import { useState, useEffect, useCallback } from 'react';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Radio, Loader2, Send, Trash2, Eye, EyeOff, AlertOctagon, AlertTriangle, Info } from 'lucide-react';
import {
  listAllBroadcasts,
  createBroadcast,
  setBroadcastActive,
  deleteBroadcast,
  type Broadcast,
  type BroadcastSeverity,
} from '../../lib/broadcasts';

const SEVERITIES: { value: BroadcastSeverity; label: string; Icon: typeof Info; cls: string }[] = [
  { value: 'info', label: 'Info', Icon: Info, cls: 'text-info border-info/30' },
  { value: 'warning', label: 'Warning', Icon: AlertTriangle, cls: 'text-gold border-gold/30' },
  { value: 'critical', label: 'Critical', Icon: AlertOctagon, cls: 'text-danger border-danger/30' },
];

export const BroadcastsManager = () => {
  const [list, setList] = useState<Broadcast[]>([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<BroadcastSeverity>('info');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const load = useCallback(async () => {
    try {
      setList(await listAllBroadcasts());
    } catch (e) {
      toast.error(`Load failed: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async (activate: boolean) => {
    if (!title.trim()) return toast.error('Title is required');
    if (!message.trim()) return toast.error('Message is required');
    setSaving(true);
    try {
      await createBroadcast({
        title: title.trim(),
        message: message.trim(),
        severity,
        is_active: activate,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      });
      toast.success(activate ? 'Broadcast sent' : 'Saved (inactive)');
      setTitle(''); setMessage(''); setSeverity('info'); setStartsAt(''); setEndsAt('');
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (b: Broadcast) => {
    setBusyId(b.id);
    try {
      await setBroadcastActive(b.id, !b.is_active);
      setList(prev => prev.map(x => x.id === b.id ? { ...x, is_active: !x.is_active } : x));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (b: Broadcast) => {
    if (!await confirm({ title: 'Delete broadcast?', message: `"${b.title}" will be removed for everyone.`, confirmText: 'Delete', isDangerous: true })) return;
    setBusyId(b.id);
    try {
      await deleteBroadcast(b.id);
      setList(prev => prev.filter(x => x.id !== b.id));
      toast.success('Deleted');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const inputCls = 'w-full rounded border border-danger/30 bg-scrim/40 px-3 py-2 text-sm text-fg outline-none focus:border-danger/50';

  return (
    <div className="min-h-screen bg-sunken text-fg">
      <header className="sticky top-0 z-50 border-b border-danger/20 bg-scrim/50 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg border border-danger/20 bg-danger/10 p-2">
              <Radio className="h-5 w-5 text-danger" />
            </div>
            <div>
              <h1 className="text-lg font-bold uppercase tracking-widest text-danger">Broadcasts</h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-danger/40">Platform-wide announcements</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-2 rounded border border-danger/20 px-4 py-2 text-xs uppercase tracking-widest text-danger transition-colors hover:bg-danger/10">
            <ArrowLeft className="h-3 w-3" /> Cockpit
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 space-y-10">
        {/* Composer */}
        <section className="rounded-xl border border-danger/20 bg-tint/[0.02] p-6">
          <h2 className="mb-5 font-mono text-sm uppercase tracking-widest text-danger/70">New broadcast</h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Title</label>
              <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="Scheduled maintenance" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Message</label>
              <textarea className={`${inputCls} min-h-[90px]`} value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Maintenance is scheduled for 18 July 2026 between 1am and 3am. Usage will be restricted during this window." />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Severity</label>
                <div className="flex gap-2">
                  {SEVERITIES.map(s => (
                    <button key={s.value} type="button" onClick={() => setSeverity(s.value)}
                      className={`flex-1 rounded border px-2 py-2 text-xs font-medium transition-colors ${severity === s.value ? `${s.cls} bg-tint/5` : 'border-line text-fg-4 hover:text-fg-2'}`}>
                      <s.Icon className="mx-auto mb-1 h-4 w-4" />
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Starts (optional)</label>
                <input type="datetime-local" className={inputCls} value={startsAt} onChange={e => setStartsAt(e.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Ends (optional)</label>
                <input type="datetime-local" className={inputCls} value={endsAt} onChange={e => setEndsAt(e.target.value)} />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
              <button onClick={() => publish(true)} disabled={saving}
                className="inline-flex items-center gap-2 rounded bg-danger/20 border border-danger/40 px-5 py-2.5 text-sm font-semibold text-danger hover:bg-danger/30 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send broadcast
              </button>
              <button onClick={() => publish(false)} disabled={saving}
                className="inline-flex items-center gap-2 rounded border border-line-strong px-5 py-2.5 text-sm font-semibold text-fg-2 hover:bg-tint/5 disabled:opacity-50">
                Save inactive
              </button>
            </div>
          </div>
        </section>

        {/* List */}
        <section>
          <h2 className="mb-4 font-mono text-sm uppercase tracking-widest text-danger/70">All broadcasts</h2>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-danger" /></div>
          ) : list.length === 0 ? (
            <p className="py-8 text-center text-sm text-fg-4">No broadcasts yet.</p>
          ) : (
            <div className="space-y-3">
              {list.map(b => {
                const sev = SEVERITIES.find(s => s.value === b.severity) ?? SEVERITIES[0];
                return (
                  <div key={b.id} className="flex items-start gap-4 rounded-lg border border-line/60 bg-tint/[0.01] p-4">
                    <sev.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${sev.cls.split(' ')[0]}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-fg">{b.title}</p>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${b.is_active ? 'bg-success/15 text-success' : 'bg-tint/5 text-fg-4'}`}>
                          {b.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-fg-3 whitespace-pre-line">{b.message}</p>
                      <p className="mt-1.5 text-[11px] text-fg-4">
                        {new Date(b.created_at).toLocaleString('en-GB')}
                        {b.starts_at && ` · from ${new Date(b.starts_at).toLocaleString('en-GB')}`}
                        {b.ends_at && ` · until ${new Date(b.ends_at).toLocaleString('en-GB')}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button onClick={() => toggle(b)} disabled={busyId === b.id} title={b.is_active ? 'Deactivate' : 'Activate'}
                        className="rounded p-2 text-fg-3 hover:bg-tint/5 hover:text-fg disabled:opacity-50">
                        {b.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button onClick={() => remove(b)} disabled={busyId === b.id} title="Delete"
                        className="rounded p-2 text-fg-3 hover:bg-danger/10 hover:text-danger disabled:opacity-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
